import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import toastr from 'toastr';
import 'toastr/build/toastr.min.css';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  FaArrowLeft, 
  FaSave, 
  FaQuestionCircle, 
  FaExclamationTriangle, 
  FaCheckCircle,
  FaBuilding,
  FaBolt,
  FaFire,
  FaCar,
  FaChartLine,
  FaFileUpload,
  FaInfoCircle,
  FaSearch,
  FaTimes,
  FaPlus,
  FaTrash,
  FaIndustry,
  FaWind
} from 'react-icons/fa';

const localeDateTimeOpts = {
  year: 'numeric',
  month: 'long',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit'
};

/** Avoid "Invalid Date" when API sends snake_case or missing timestamps */
function formatSavedTimestamp(answerRow) {
  if (!answerRow) return null;
  const raw =
    answerRow.updatedAt ??
    answerRow.updated_at ??
    answerRow.createdAt ??
    answerRow.created_at;
  if (raw == null || raw === '') return null;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString('en-US', localeDateTimeOpts);
}

function formatIsoTimestamp(iso) {
  if (iso == null || iso === '') return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString('en-US', localeDateTimeOpts);
}

const EmissionsQuestionsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const category = searchParams.get('category');
  const scope = searchParams.get('scope');

  const [questionSets, setQuestionSets] = useState([]);
  const [selectedQuestionSet, setSelectedQuestionSet] = useState(null);
  const [currentQuestions, setCurrentQuestions] = useState([]);
  const [usingFallback, setUsingFallback] = useState(false);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [userAnswers, setUserAnswers] = useState(null);
  const [answeredStatus, setAnsweredStatus] = useState({});
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchField, setSearchField] = useState(null); // Track which field is being searched

  useEffect(() => {
    fetchQuestionSets();
  }, []);

  useEffect(() => {
    if (category) {
      fetchQuestionSetByCategory();
    }
  }, [category, scope]);

  useEffect(() => {
    if (selectedQuestionSet) {
      loadUserAnswers();
      // Initialize questions immediately, even if answers haven't loaded yet
      initializeQuestions();
    }
  }, [selectedQuestionSet]);

  useEffect(() => {
    // Re-initialize questions when answers are loaded/updated
    if (selectedQuestionSet && userAnswers !== null) {
      initializeQuestions();
    }
  }, [userAnswers]);

  const fetchQuestionSets = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/emissions-questions/');
      if (response.data.status) {
        const sets = response.data.questionSets || [];
        
        // Sort to ensure organization_context appears first
        const sortedSets = [...sets].sort((a, b) => {
          // organization_context always comes first
          if (a.category === 'organization_context' && b.category !== 'organization_context') {
            return -1;
          }
          if (a.category !== 'organization_context' && b.category === 'organization_context') {
            return 1;
          }
          // For non-organization_context items, sort alphabetically by category, then by scope
          if (a.category !== b.category) {
            return a.category.localeCompare(b.category);
          }
          return a.scope - b.scope;
        });
        
        setQuestionSets(sortedSets);
        
        // Check answered status for each question set
        const statusMap = {};
        for (const qs of sortedSets) {
          try {
            const answerResponse = await axios.get(`/emissions-questions/${qs.id}/answers`);
            statusMap[qs.id] = answerResponse.data.status && answerResponse.data.answers.length > 0;
          } catch (error) {
            statusMap[qs.id] = false;
          }
        }
        setAnsweredStatus(statusMap);
        
        // If category is provided, auto-select that question set
        if (category) {
          const found = sortedSets.find(
            qs => qs.category === category && (!scope || qs.scope === parseInt(scope))
          );
          if (found) {
            setSelectedQuestionSet(found);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching question sets:', error);
      toastr.error(t('reporting.emissionsQuestions.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const fetchQuestionSetByCategory = async () => {
    setLoading(true);
    try {
      const url = scope 
        ? `/emissions-questions/category/${category}?scope=${scope}`
        : `/emissions-questions/category/${category}`;
      const response = await axios.get(url);
      if (response.data.status) {
        setSelectedQuestionSet(response.data.questionSet);
      } else {
        toastr.error(response.data.message || t('reporting.emissionsQuestions.questionSetNotFound'));
      }
    } catch (error) {
      console.error('Error fetching question set:', error);
      toastr.error(t('reporting.emissionsQuestions.failedToLoadQuestionSet'));
    } finally {
      setLoading(false);
    }
  };

  const loadUserAnswers = async () => {
    if (!selectedQuestionSet) return;
    try {
      const response = await axios.get(`/emissions-questions/${selectedQuestionSet.id}/answers`);
      if (response.data.status && response.data.answers && response.data.answers.length > 0) {
        const latestAnswer = response.data.answers[0];
        setUserAnswers(latestAnswer);
        setAnswers(latestAnswer.answers || {});
      } else {
        setUserAnswers(null);
        setAnswers({});
      }
    } catch (error) {
      // Silently fail - questions should still render even if answers can't be loaded
      console.warn('Could not load user answers (this is okay if no answers exist yet):', error.message);
      setUserAnswers(null);
      setAnswers({});
    }
  };

  const initializeQuestions = () => {
    if (!selectedQuestionSet) {
      setCurrentQuestions([]);
      return;
    }

    // Always start with primary questions
    const primaryQuestions = Array.isArray(selectedQuestionSet.subQuestions) 
      ? selectedQuestionSet.subQuestions 
      : [];
    
    // If no primary questions, check fallback
    if (primaryQuestions.length === 0 && selectedQuestionSet.fallbackQuestions) {
      const fallbackQuestions = Array.isArray(selectedQuestionSet.fallbackQuestions)
        ? selectedQuestionSet.fallbackQuestions
        : [];
      setCurrentQuestions(fallbackQuestions);
      setUsingFallback(true);
      
      // Initialize default values for fallback questions
      const defaultAnswers = {};
      fallbackQuestions.forEach(q => {
        if (q.defaultValue !== undefined && q.defaultValue !== null && q.defaultValue !== '') {
          defaultAnswers[q.field] = q.defaultValue;
        }
      });
      if (Object.keys(defaultAnswers).length > 0) {
        setAnswers(prev => ({ ...prev, ...defaultAnswers }));
      }
      return;
    }
    
    // Check if user has existing answers
    if (userAnswers && userAnswers.answers) {
      // Check if primary questions are fully answered
      const requiredPrimaryQuestions = primaryQuestions.filter(q => q.required !== false);
      const allPrimaryAnswered = requiredPrimaryQuestions.every(q => {
        const answer = userAnswers.answers[q.field];
        return answer !== undefined && answer !== null && answer !== '';
      });

      if (allPrimaryAnswered && requiredPrimaryQuestions.length > 0) {
        // Primary questions are answered, show them
        setCurrentQuestions(primaryQuestions);
        setUsingFallback(false);
      } else if (selectedQuestionSet.fallbackQuestions && selectedQuestionSet.fallbackQuestions.length > 0) {
        // Primary not fully answered, check if fallback is being used
        const fallbackFields = selectedQuestionSet.fallbackQuestions.map(q => q.field);
        const hasFallbackAnswers = fallbackFields.some(field => userAnswers.answers[field]);
        
        if (hasFallbackAnswers) {
          setCurrentQuestions(selectedQuestionSet.fallbackQuestions);
          setUsingFallback(true);
        } else {
          // Show primary questions for user to complete
          setCurrentQuestions(primaryQuestions);
          setUsingFallback(false);
        }
      } else {
        setCurrentQuestions(primaryQuestions);
        setUsingFallback(false);
      }
      
      // Ensure default values are included in answers
      const defaultAnswers = {};
      primaryQuestions.forEach(q => {
        if (q.defaultValue !== undefined && q.defaultValue !== null && q.defaultValue !== '') {
          // Only add if not already in userAnswers
          if (!userAnswers.answers || !userAnswers.answers[q.field]) {
            defaultAnswers[q.field] = q.defaultValue;
          }
        }
      });
      if (Object.keys(defaultAnswers).length > 0) {
        setAnswers(prev => ({ ...prev, ...defaultAnswers }));
      }
    } else {
      // No existing answers, show primary questions first
      setCurrentQuestions(primaryQuestions);
      setUsingFallback(false);
      
      // Initialize answers with default values for fields that have them
      const defaultAnswers = {};
      primaryQuestions.forEach(q => {
        if (q.defaultValue !== undefined && q.defaultValue !== null) {
          defaultAnswers[q.field] = q.defaultValue;
        }
      });
      if (Object.keys(defaultAnswers).length > 0) {
        setAnswers(prev => ({ ...prev, ...defaultAnswers }));
      }
    }
  };

  const handleAnswerChange = (field, value) => {
    setAnswers(prev => ({ ...prev, [field]: value }));
  };

  const handleSearchFactors = async () => {
    if (!searchQuery.trim()) {
      toastr.warning('Please enter a search query');
      return;
    }

    setSearching(true);
    try {
      const response = await axios.get('/emissions-questions/search/factors', {
        params: {
          query: searchQuery.trim(),
          region: 'CL',
          access_type: 'public',
          results_per_page: 10
        }
      });

      if (response.data.status) {
        setSearchResults(response.data.results || []);
        if (response.data.results.length === 0) {
          toastr.info('No emission factors found. Try a different search term.');
        }
      } else {
        toastr.error(response.data.message || 'Failed to search emission factors');
        setSearchResults([]);
      }
    } catch (error) {
      console.error('Error searching emission factors:', error);
      toastr.error(error.response?.data?.error || 'Failed to search emission factors');
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  const handleSelectFactor = (factor) => {
    const pipeIdx = searchField != null ? searchField.lastIndexOf('|') : -1;
    if (pipeIdx > 0) {
      const tableField = searchField.slice(0, pipeIdx);
      const rowIndex = parseInt(searchField.slice(pipeIdx + 1), 10);
      if (!Number.isNaN(rowIndex)) {
        const currentRows = [...(answers[tableField] || [])];
        if (!currentRows[rowIndex]) currentRows[rowIndex] = {};
        currentRows[rowIndex].emission_factor_id = factor.id;
        handleAnswerChange(tableField, currentRows);
        setShowSearchModal(false);
        setSearchQuery('');
        setSearchResults([]);
        setSearchField(null);
        toastr.success('Emission factor selected');
        return;
      }
    }
    if (searchField === 'emission_factor_id') {
      handleAnswerChange('emission_factor_id', factor.id);
    } else if (searchField === 'activity_id') {
      handleAnswerChange('activity_id', factor.activity_id);
    }
    setShowSearchModal(false);
    setSearchQuery('');
    setSearchResults([]);
    setSearchField(null);
    toastr.success('Emission factor selected');
  };

  const openSearchModal = (field) => {
    setSearchField(field);
    setShowSearchModal(true);
    setSearchQuery('');
    setSearchResults([]);
  };

  const renderQuestionInput = (question) => {
    const value = answers[question.field] || question.defaultValue || '';
    const isRequired = question.required !== false;

    switch (question.type) {
      case 'date':
        return (
          <input
            type="date"
            value={value}
            onChange={(e) => handleAnswerChange(question.field, e.target.value)}
            required={isRequired}
            className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-green-500 focus:ring-2 focus:ring-green-200 transition-all bg-white"
          />
        );

      case 'number':
        return (
          <div className="relative">
            <input
              type="number"
              value={value}
              onChange={(e) => handleAnswerChange(question.field, e.target.value)}
              required={isRequired}
              min={question.min}
              max={question.max}
              step={question.step || 1}
              placeholder={question.helpText || t('reporting.emissionsQuestions.enterNumber')}
              className="w-full px-4 py-3 pr-20 border-2 border-gray-300 rounded-xl focus:border-green-500 focus:ring-2 focus:ring-green-200 transition-all bg-white"
            />
            {question.unit && (
              <span className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-600 font-medium text-sm">
                {question.unit}
              </span>
            )}
          </div>
        );

      case 'select':
        return (
          <select
            value={value}
            onChange={(e) => handleAnswerChange(question.field, e.target.value)}
            required={isRequired}
            disabled={question.readOnly}
            className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-green-500 focus:ring-2 focus:ring-green-200 transition-all bg-white disabled:bg-gray-100 disabled:cursor-not-allowed"
          >
            <option value="">{question.placeholder || t('reporting.emissionsQuestions.selectOption')}</option>
            {question.options?.map((opt, idx) => (
              <option key={idx} value={opt.value}>
                {opt.label || opt.value}
              </option>
            ))}
          </select>
        );

      case 'radio':
        return (
          <div className="space-y-3">
            {question.options?.map((opt, idx) => {
              const isChecked = value === opt.value;
              return (
                <label
                  key={idx}
                  className={`flex items-center p-4 border-2 rounded-xl cursor-pointer transition-all ${
                    isChecked
                      ? 'border-green-500 bg-green-50 shadow-md'
                      : 'border-gray-300 hover:border-green-400 bg-white'
                  }`}
                >
                  <input
                    type="radio"
                    name={question.field}
                    value={opt.value}
                    checked={isChecked}
                    onChange={(e) => handleAnswerChange(question.field, e.target.value)}
                    required={isRequired}
                    className="mr-3 w-5 h-5 text-green-600 focus:ring-2 focus:ring-green-200"
                  />
                  <span className={`font-medium ${isChecked ? 'text-green-800' : 'text-gray-700'}`}>
                    {opt.label}
                  </span>
                </label>
              );
            })}
          </div>
        );

      case 'checkbox':
        const isChecked = value === question.value || value === true;
        return (
          <label className={`flex items-center p-4 border-2 rounded-xl cursor-pointer transition-all ${
            isChecked
              ? 'border-green-500 bg-green-50 shadow-md'
              : 'border-gray-300 hover:border-green-400 bg-white'
          }`}>
            <input
              type="checkbox"
              checked={isChecked}
              onChange={(e) => handleAnswerChange(question.field, e.target.checked ? question.value : false)}
              required={isRequired}
              className="mr-3 w-5 h-5 text-green-600 focus:ring-2 focus:ring-green-200"
            />
            <span className={`font-medium ${isChecked ? 'text-green-800' : 'text-gray-700'}`}>
              {question.label || question.question}
            </span>
          </label>
        );

      case 'multiselect':
        return (
          <div className="space-y-3">
            <div className="flex items-center space-x-2">
              <input
                type="text"
                placeholder={question.placeholder || t('reporting.emissionsQuestions.typeAndPressEnter')}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && e.target.value.trim()) {
                    e.preventDefault();
                    const newValue = e.target.value.trim();
                    const currentValues = Array.isArray(value) ? value : [];
                    if (!currentValues.includes(newValue)) {
                      handleAnswerChange(question.field, [...currentValues, newValue]);
                    }
                    e.target.value = '';
                  }
                }}
                className="flex-1 px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-green-500 focus:ring-2 focus:ring-green-200 transition-all bg-white"
              />
              <button
                type="button"
                onClick={(e) => {
                  const input = e.target.previousElementSibling;
                  if (input.value.trim()) {
                    const newValue = input.value.trim();
                    const currentValues = Array.isArray(value) ? value : [];
                    if (!currentValues.includes(newValue)) {
                      handleAnswerChange(question.field, [...currentValues, newValue]);
                    }
                    input.value = '';
                  }
                }}
                className="px-4 py-3 bg-green-500 text-white rounded-xl hover:bg-green-600 transition-all font-semibold"
              >
                {t('reporting.emissionsQuestions.add')}
              </button>
            </div>
            {Array.isArray(value) && value.length > 0 && (
              <div className="flex flex-wrap gap-2 p-3 bg-gray-50 rounded-xl border border-gray-200">
                {value.map((item, idx) => (
                  <span
                    key={idx}
                    className="px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-full flex items-center gap-2 font-medium shadow-sm"
                  >
                    {item}
                    <button
                      type="button"
                      onClick={() => {
                        const newValues = value.filter((_, i) => i !== idx);
                        handleAnswerChange(question.field, newValues);
                      }}
                      className="w-5 h-5 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-all"
                      title={t('reporting.emissionsQuestions.remove')}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        );

      case 'file':
        return (
          <div className="space-y-3">
            <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer bg-gray-50 hover:bg-gray-100 hover:border-green-400 transition-all group">
              <div className="flex flex-col items-center justify-center pt-5 pb-6">
                <FaFileUpload className="w-10 h-10 text-gray-400 group-hover:text-green-500 mb-2" />
                <p className="mb-2 text-sm font-semibold text-gray-600 group-hover:text-green-600">
                  {question.multiple ? t('reporting.emissionsQuestions.clickToUploadFiles') : t('reporting.emissionsQuestions.clickToUploadFile')}
                </p>
                <p className="text-xs text-gray-500">
                  {question.accept ? `${t('reporting.emissionsQuestions.accepted')}: ${question.accept}` : t('reporting.emissionsQuestions.anyFileType')}
                </p>
              </div>
              <input
                type="file"
                onChange={(e) => {
                  const files = Array.from(e.target.files);
                  if (question.multiple) {
                    handleAnswerChange(question.field, files.map(f => f.name));
                  } else {
                    handleAnswerChange(question.field, files[0]?.name || '');
                  }
                }}
                multiple={question.multiple}
                accept={question.accept}
                required={isRequired}
                className="hidden"
              />
            </label>
            {Array.isArray(value) && value.length > 0 && (
              <div className="space-y-2">
                {value.map((fileName, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                    <span className="text-sm font-medium text-green-800">{fileName}</span>
                    <button
                      type="button"
                      onClick={() => {
                        const newFiles = value.filter((_, i) => i !== idx);
                        handleAnswerChange(question.field, newFiles);
                      }}
                      className="text-red-500 hover:text-red-700"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
            {typeof value === 'string' && value && (
              <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                <span className="text-sm font-medium text-green-800">{value}</span>
                <button
                  type="button"
                  onClick={() => handleAnswerChange(question.field, '')}
                  className="text-red-500 hover:text-red-700"
                >
                  ×
                </button>
              </div>
            )}
            {question.helpText && (
              <p className="text-xs text-gray-500 flex items-center space-x-1">
                <FaInfoCircle className="w-3 h-3" />
                <span>{question.helpText}</span>
              </p>
            )}
          </div>
        );

      case 'batch_table':
        return (
          <BatchEstimateTable
            value={value || []}
            onChange={(newValue) => handleAnswerChange(question.field, newValue)}
            columns={question.columns || []}
            onSearchFactor={(rowIndex) => {
              setSearchField(`${question.field}|${rowIndex}`);
              setShowSearchModal(true);
              setSearchQuery('');
              setSearchResults([]);
            }}
            required={isRequired}
          />
        );

      case 'text':
      default:
        // Special handling for emission_factor_id field - add search button
        if (question.field === 'emission_factor_id' || question.field === 'activity_id') {
          return (
            <div className="space-y-3">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={value}
                  onChange={(e) => handleAnswerChange(question.field, e.target.value)}
                  required={isRequired}
                  disabled={question.readOnly}
                  placeholder={question.placeholder || question.helpText || t('reporting.emissionsQuestions.enterText')}
                  className="flex-1 px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-green-500 focus:ring-2 focus:ring-green-200 transition-all bg-white disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
                <button
                  type="button"
                  onClick={() => openSearchModal(question.field)}
                  className="px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-xl hover:from-blue-600 hover:to-indigo-700 transition-all font-semibold flex items-center space-x-2 shadow-lg hover:shadow-xl"
                  title="Search for emission factors"
                >
                  <FaSearch className="w-4 h-4" />
                  <span>Search</span>
                </button>
              </div>
              <p className="text-xs text-gray-500 flex items-start space-x-1">
                <FaInfoCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                <span>Click "Search" to find emission factors by activity name, category, or description. Select a result to auto-fill the ID.</span>
              </p>
            </div>
          );
        }
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => handleAnswerChange(question.field, e.target.value)}
            required={isRequired}
            disabled={question.readOnly}
            placeholder={question.placeholder || question.helpText || t('reporting.emissionsQuestions.enterText')}
            className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-green-500 focus:ring-2 focus:ring-green-200 transition-all bg-white disabled:bg-gray-100 disabled:cursor-not-allowed"
          />
        );
    }
  };

  /** @returns {Promise<boolean>} true if answers were saved successfully */
  const handleSave = async () => {
    if (!selectedQuestionSet) {
      toastr.error(t('reporting.emissionsQuestions.noQuestionSetSelected'));
      return false;
    }

    // Validate required fields
    const requiredFields = currentQuestions.filter(q => q.required !== false);
    const missingFields = requiredFields.filter(q => {
      const answer = answers[q.field];
      // Check if answer exists, or if there's a defaultValue (for read-only fields)
      const hasAnswer = answer !== undefined && answer !== null && answer !== '';
      const hasDefault = q.defaultValue !== undefined && q.defaultValue !== null && q.defaultValue !== '';
      return !hasAnswer && !hasDefault;
    });

    if (missingFields.length > 0) {
      toastr.error(`${t('reporting.emissionsQuestions.pleaseAnswerAllRequired')}: ${missingFields.map(q => q.question).join(', ')}`);
      return false;
    }

    setSaving(true);
    try {
      // Include default values for fields that have them but aren't in answers
      const answersToSubmit = { ...answers };
      currentQuestions.forEach(q => {
        if (q.defaultValue !== undefined && q.defaultValue !== null && q.defaultValue !== '') {
          // Only add if not already present or if it's a read-only field
          if (!answersToSubmit[q.field] || q.readOnly) {
            answersToSubmit[q.field] = q.defaultValue;
          }
        }
      });

      currentQuestions.forEach((q) => {
        if (
          q.type === 'batch_table' &&
          Array.isArray(q.columns) &&
          Array.isArray(answersToSubmit[q.field])
        ) {
          answersToSubmit[q.field] = answersToSubmit[q.field].map((row) => {
            const merged = { ...(row || {}) };
            q.columns.forEach((col) => {
              const empty =
                merged[col.field] === undefined ||
                merged[col.field] === null ||
                merged[col.field] === '';
              if (
                empty &&
                col.defaultValue !== undefined &&
                col.defaultValue !== null &&
                col.defaultValue !== ''
              ) {
                merged[col.field] = col.defaultValue;
              }
            });
            return merged;
          });
        }
      });
      
      const response = await axios.post('/emissions-questions/submit-answers', {
        questionSetId: selectedQuestionSet.id,
        answers: answersToSubmit
      });

      if (response.data.status) {
        toastr.success(t('reporting.emissionsQuestions.answersSavedSuccessfully'));
        setUserAnswers(response.data.answer);
        // Update answered status
        setAnsweredStatus(prev => ({ ...prev, [selectedQuestionSet.id]: true }));
        // Reload to show updated state
        await loadUserAnswers();
        return true;
      } else {
        toastr.error(response.data.message || t('reporting.emissionsQuestions.failedToSaveAnswers'));
        return false;
      }
    } catch (error) {
      console.error('Error saving answers:', error);
      toastr.error(error.response?.data?.error || t('reporting.emissionsQuestions.failedToSaveAnswers'));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleCalculate = async () => {
    if (!selectedQuestionSet) {
      toastr.error(t('reporting.emissionsQuestions.noQuestionSetSelected'));
      return;
    }

    // Calculate reads from DB — always persist current form state first (not only on first save).
    const saved = await handleSave();
    if (!saved) return;

    setSaving(true);
    try {
      const response = await axios.post(`/emissions-questions/${selectedQuestionSet.id}/calculate`);

      if (response.data.status) {
        toastr.success(t('reporting.emissionsQuestions.emissionsCalculatedSuccessfully') || 'Emissions calculated successfully');
        
        // Update answers with calculation result
        if (response.data.answer) {
          setUserAnswers(response.data.answer);
          setAnswers(response.data.answer.answers || answers);
        }
        
        // Show calculation result
        if (response.data.calculation) {
          const calc = response.data.calculation;
          toastr.info(
            `Calculated: ${calc.co2e_tons.toFixed(2)} ${calc.co2e_unit}`,
            'Emissions Calculation Result',
            { timeOut: 10000 }
          );
        }
      } else {
        toastr.error(response.data.message || t('reporting.emissionsQuestions.failedToCalculate') || 'Failed to calculate emissions');
      }
    } catch (error) {
      console.error('Error calculating emissions:', error);
      toastr.error(error.response?.data?.error || t('reporting.emissionsQuestions.failedToCalculate') || 'Failed to calculate emissions');
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    navigate('/home');
  };

  const getCategoryLabel = (category) => {
    const labels = {
      'organization_context': t('reporting.emissionsQuestions.categoryLabels.organization_context'),
      'grid_electricity': t('reporting.emissionsQuestions.categoryLabels.grid_electricity'),
      'stationary_fuel': t('reporting.emissionsQuestions.categoryLabels.stationary_fuel'),
      'mobile_fuel': t('reporting.emissionsQuestions.categoryLabels.mobile_fuel'),
      'heat': t('reporting.emissionsQuestions.categoryLabels.heat') || 'Purchased Heat & Steam',
      'steam': t('reporting.emissionsQuestions.categoryLabels.heat') || 'Purchased Heat & Steam',
      'purchased_heat': t('reporting.emissionsQuestions.categoryLabels.heat') || 'Purchased Heat & Steam',
      'waste': t('reporting.emissionsQuestions.categoryLabels.waste') || 'Waste Treatment',
      'water': t('reporting.emissionsQuestions.categoryLabels.water') || 'Water Supply/Treatment',
      'transport': t('reporting.emissionsQuestions.categoryLabels.transport') || 'Transport Emissions',
      'batch': t('reporting.emissionsQuestions.categoryLabels.batch') || 'Batch Estimates',
      'process_emissions': t('reporting.emissionsQuestions.categoryLabels.process_emissions'),
      'scope1_process': t('reporting.emissionsQuestions.categoryLabels.process_emissions'),
      'fugitive_emissions': t('reporting.emissionsQuestions.categoryLabels.fugitive_emissions'),
      'scope1_fugitive': t('reporting.emissionsQuestions.categoryLabels.fugitive_emissions'),
      'fugitive': t('reporting.emissionsQuestions.categoryLabels.fugitive_emissions')
    };
    return labels[category] || category;
  };

  const getCategoryIcon = (category) => {
    const icons = {
      'organization_context': FaBuilding,
      'grid_electricity': FaBolt,
      'stationary_fuel': FaFire,
      'mobile_fuel': FaCar,
      'heat': FaFire,
      'steam': FaFire,
      'purchased_heat': FaFire,
      'waste': FaChartLine,
      'water': FaChartLine,
      'transport': FaCar,
      'batch': FaChartLine,
      'process_emissions': FaIndustry,
      'scope1_process': FaIndustry,
      'fugitive_emissions': FaWind,
      'scope1_fugitive': FaWind,
      'fugitive': FaWind
    };
    return icons[category] || FaQuestionCircle;
  };

  const getCategoryColor = (category) => {
    const colors = {
      'organization_context': {
        bg: 'from-blue-500 to-blue-600',
        border: 'border-blue-300',
        hover: 'hover:border-blue-400',
        selected: 'border-blue-500 bg-blue-50',
        icon: 'text-blue-600',
        badge: 'bg-blue-100 text-blue-800'
      },
      'grid_electricity': {
        bg: 'from-yellow-500 to-orange-500',
        border: 'border-yellow-300',
        hover: 'hover:border-yellow-400',
        selected: 'border-yellow-500 bg-yellow-50',
        icon: 'text-yellow-600',
        badge: 'bg-yellow-100 text-yellow-800'
      },
      'stationary_fuel': {
        bg: 'from-red-500 to-red-600',
        border: 'border-red-300',
        hover: 'hover:border-red-400',
        selected: 'border-red-500 bg-red-50',
        icon: 'text-red-600',
        badge: 'bg-red-100 text-red-800'
      },
      'mobile_fuel': {
        bg: 'from-purple-500 to-purple-600',
        border: 'border-purple-300',
        hover: 'hover:border-purple-400',
        selected: 'border-purple-500 bg-purple-50',
        icon: 'text-purple-600',
        badge: 'bg-purple-100 text-purple-800'
      },
      'heat': {
        bg: 'from-orange-500 to-red-500',
        border: 'border-orange-300',
        hover: 'hover:border-orange-400',
        selected: 'border-orange-500 bg-orange-50',
        icon: 'text-orange-600',
        badge: 'bg-orange-100 text-orange-800'
      },
      'steam': {
        bg: 'from-orange-500 to-red-500',
        border: 'border-orange-300',
        hover: 'hover:border-orange-400',
        selected: 'border-orange-500 bg-orange-50',
        icon: 'text-orange-600',
        badge: 'bg-orange-100 text-orange-800'
      },
      'purchased_heat': {
        bg: 'from-orange-500 to-red-500',
        border: 'border-orange-300',
        hover: 'hover:border-orange-400',
        selected: 'border-orange-500 bg-orange-50',
        icon: 'text-orange-600',
        badge: 'bg-orange-100 text-orange-800'
      },
      'waste': {
        bg: 'from-gray-500 to-gray-600',
        border: 'border-gray-300',
        hover: 'hover:border-gray-400',
        selected: 'border-gray-500 bg-gray-50',
        icon: 'text-gray-600',
        badge: 'bg-gray-100 text-gray-800'
      },
      'water': {
        bg: 'from-cyan-500 to-blue-500',
        border: 'border-cyan-300',
        hover: 'hover:border-cyan-400',
        selected: 'border-cyan-500 bg-cyan-50',
        icon: 'text-cyan-600',
        badge: 'bg-cyan-100 text-cyan-800'
      },
      'transport': {
        bg: 'from-indigo-500 to-purple-500',
        border: 'border-indigo-300',
        hover: 'hover:border-indigo-400',
        selected: 'border-indigo-500 bg-indigo-50',
        icon: 'text-indigo-600',
        badge: 'bg-indigo-100 text-indigo-800'
      },
      'batch': {
        bg: 'from-teal-500 to-green-500',
        border: 'border-teal-300',
        hover: 'hover:border-teal-400',
        selected: 'border-teal-500 bg-teal-50',
        icon: 'text-teal-600',
        badge: 'bg-teal-100 text-teal-800'
      },
      'process_emissions': {
        bg: 'from-amber-500 to-orange-600',
        border: 'border-amber-300',
        hover: 'hover:border-amber-400',
        selected: 'border-amber-500 bg-amber-50',
        icon: 'text-amber-700',
        badge: 'bg-amber-100 text-amber-900'
      },
      'scope1_process': {
        bg: 'from-amber-500 to-orange-600',
        border: 'border-amber-300',
        hover: 'hover:border-amber-400',
        selected: 'border-amber-500 bg-amber-50',
        icon: 'text-amber-700',
        badge: 'bg-amber-100 text-amber-900'
      },
      'fugitive_emissions': {
        bg: 'from-sky-500 to-blue-600',
        border: 'border-sky-300',
        hover: 'hover:border-sky-400',
        selected: 'border-sky-500 bg-sky-50',
        icon: 'text-sky-700',
        badge: 'bg-sky-100 text-sky-900'
      },
      'scope1_fugitive': {
        bg: 'from-sky-500 to-blue-600',
        border: 'border-sky-300',
        hover: 'hover:border-sky-400',
        selected: 'border-sky-500 bg-sky-50',
        icon: 'text-sky-700',
        badge: 'bg-sky-100 text-sky-900'
      },
      'fugitive': {
        bg: 'from-sky-500 to-blue-600',
        border: 'border-sky-300',
        hover: 'hover:border-sky-400',
        selected: 'border-sky-500 bg-sky-50',
        icon: 'text-sky-700',
        badge: 'bg-sky-100 text-sky-900'
      }
    };
    return colors[category] || colors['organization_context'];
  };

  const getScopeBadgeColor = (scope) => {
    const colors = {
      0: 'bg-gray-100 text-gray-800',
      1: 'bg-green-100 text-green-800',
      2: 'bg-blue-100 text-blue-800'
    };
    return colors[scope] || colors[0];
  };

  const checkIfAnswered = async (questionSetId) => {
    try {
      const response = await axios.get(`/emissions-questions/${questionSetId}/answers`);
      return response.data.status && response.data.answers.length > 0;
    } catch (error) {
      return false;
    }
  };

  if (loading && !selectedQuestionSet) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto mb-4"></div>
          <p className="text-gray-600">{t('reporting.emissionsQuestions.loadingQuestions')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-green-600 shadow-lg">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={handleBack}
                className="bg-white/20 backdrop-blur-sm rounded-xl p-3 hover:bg-white/30 transition-all duration-200"
              >
                <FaArrowLeft className="w-5 h-5 text-white" />
              </button>
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                  <FaQuestionCircle className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-white text-2xl font-semibold">{t('reporting.emissionsQuestions.title')}</h1>
                  <p className="text-blue-100 text-sm">
                    {selectedQuestionSet ? getCategoryLabel(selectedQuestionSet.category) : t('reporting.emissionsQuestions.selectCategory')}
                    {selectedQuestionSet?.scope && ` - ${t('reporting.emissionsQuestions.scope')} ${selectedQuestionSet.scope}`}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Question Set Selector */}
        {!category && questionSets.length > 0 && (
          <div className="mb-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-800">{t('reporting.emissionsQuestions.selectQuestionSet')}</h2>
                <p className="text-gray-600 mt-1">{t('reporting.emissionsQuestions.selectCategoryDescription')}</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {questionSets.map((qs) => {
                const CategoryIcon = getCategoryIcon(qs.category);
                const colors = getCategoryColor(qs.category);
                const isAnswered = answeredStatus[qs.id];
                const isSelected = selectedQuestionSet?.id === qs.id;
                
                return (
                  <button
                    key={qs.id}
                    onClick={() => setSelectedQuestionSet(qs)}
                    className={`group relative p-6 rounded-2xl border-2 transition-all duration-300 text-left overflow-hidden ${
                      isSelected
                        ? `${colors.selected} ${colors.border} shadow-lg transform scale-105`
                        : `${colors.border} bg-white hover:shadow-xl hover:transform hover:scale-[1.02]`
                    }`}
                  >
                    {/* Background Gradient Effect */}
                    <div className={`absolute inset-0 bg-gradient-to-br ${colors.bg} opacity-0 group-hover:opacity-5 transition-opacity duration-300`}></div>
                    
                    <div className="relative z-10">
                      {/* Header with Icon and Status */}
                      <div className="flex items-start justify-between mb-4">
                        <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${colors.bg} flex items-center justify-center shadow-lg`}>
                          <CategoryIcon className="w-7 h-7 text-white" />
                        </div>
                        {isAnswered && (
                          <div className="flex items-center space-x-2 px-3 py-1 bg-green-100 rounded-full">
                            <FaCheckCircle className="w-4 h-4 text-green-600" />
                            <span className="text-xs font-semibold text-green-700">{t('reporting.emissionsQuestions.answered')}</span>
                          </div>
                        )}
                      </div>
                      
                      {/* Category Title */}
                      <h3 className="text-xl font-bold text-gray-800 mb-2 group-hover:text-gray-900 transition-colors">
                        {getCategoryLabel(qs.category)}
                      </h3>
                      
                      {/* Scope Badge */}
                      <div className="flex items-center space-x-2 mb-3">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getScopeBadgeColor(qs.scope)}`}>
                          {t('reporting.emissionsQuestions.scope')} {qs.scope}
                        </span>
                        {qs.scope === 0 && (
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                            {t('reporting.emissionsQuestions.setup')}
                          </span>
                        )}
                      </div>
                      
                      {/* Question Count */}
                      <div className="flex items-center text-sm text-gray-500">
                        <FaQuestionCircle className="w-4 h-4 mr-2" />
                        <span>
                          {qs.subQuestions?.length || 0} {qs.subQuestions?.length !== 1 ? t('reporting.emissionsQuestions.primaryQuestions') : t('reporting.emissionsQuestions.primaryQuestion')}
                          {qs.fallbackQuestions && qs.fallbackQuestions.length > 0 && (
                            <span className="ml-1">+ {qs.fallbackQuestions.length} {t('reporting.emissionsQuestions.fallback')}</span>
                          )}
                        </span>
                      </div>
                      
                      {/* Arrow Indicator */}
                      <div className="mt-4 flex items-center text-sm font-semibold text-gray-600 group-hover:text-gray-800 transition-colors">
                        <span>{t('reporting.emissionsQuestions.startAnswering')}</span>
                        <FaArrowLeft className="w-4 h-4 ml-2 transform rotate-180 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Questions Form */}
        {selectedQuestionSet && currentQuestions.length > 0 && (
          <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
            {/* Form Header */}
            <div className="bg-gradient-to-r from-blue-50 to-green-50 border-b border-gray-200 px-8 py-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  {(() => {
                    const CategoryIcon = getCategoryIcon(selectedQuestionSet.category);
                    const colors = getCategoryColor(selectedQuestionSet.category);
                    return (
                      <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${colors.bg} flex items-center justify-center shadow-md`}>
                        <CategoryIcon className="w-6 h-6 text-white" />
                      </div>
                    );
                  })()}
                  <div>
                    <h2 className="text-2xl font-bold text-gray-800">{getCategoryLabel(selectedQuestionSet.category)}</h2>
                    <div className="flex items-center space-x-3 mt-1">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getScopeBadgeColor(selectedQuestionSet.scope)}`}>
                        Scope {selectedQuestionSet.scope}
                      </span>
                      <span className="text-sm text-gray-600">
                        {currentQuestions.length} {currentQuestions.length !== 1 ? t('reporting.emissionsQuestions.questionsPlural') : t('reporting.emissionsQuestions.questions')}
                      </span>
                    </div>
                  </div>
                </div>
                {selectedQuestionSet.fallbackQuestions && selectedQuestionSet.fallbackQuestions.length > 0 && (
                  <button
                    onClick={() => {
                      if (usingFallback) {
                        setCurrentQuestions(selectedQuestionSet.subQuestions || []);
                        setUsingFallback(false);
                      } else {
                        setCurrentQuestions(selectedQuestionSet.fallbackQuestions || []);
                        setUsingFallback(true);
                      }
                    }}
                    className="px-4 py-2 bg-white border-2 border-gray-300 rounded-lg hover:border-gray-400 transition-all text-sm font-semibold text-gray-700"
                  >
                    {usingFallback ? t('reporting.emissionsQuestions.switchToPrimary') : t('reporting.emissionsQuestions.switchToFallback')}
                  </button>
                )}
              </div>
            </div>

            <div className="p-8">
              {usingFallback && (
                <div className="mb-6 p-5 bg-gradient-to-r from-yellow-50 to-orange-50 border-2 border-yellow-300 rounded-xl flex items-start space-x-4 shadow-sm">
                  <div className="flex-shrink-0">
                    <FaExclamationTriangle className="w-6 h-6 text-yellow-600" />
                  </div>
                  <div className="flex-1">
                    <p className="font-bold text-yellow-900 mb-1">{t('reporting.emissionsQuestions.usingFallbackQuestions')}</p>
                    <p className="text-sm text-yellow-800">
                      {t('reporting.emissionsQuestions.usingFallbackDescription')}
                    </p>
                  </div>
                </div>
              )}

              {userAnswers && !usingFallback && (
                <>
                  <div className="mb-6 p-5 bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-300 rounded-xl flex items-start space-x-4 shadow-sm">
                    <div className="flex-shrink-0">
                      <FaCheckCircle className="w-6 h-6 text-green-600" />
                    </div>
                    <div className="flex-1">
                      <p className="font-bold text-green-900 mb-1">{t('reporting.emissionsQuestions.answersSavedSuccessfullyTitle')}</p>
                      {(() => {
                        const savedTs = formatSavedTimestamp(userAnswers);
                        return savedTs ? (
                          <p className="text-sm text-green-800">
                            {t('reporting.emissionsQuestions.lastUpdated')}: {savedTs}
                          </p>
                        ) : null;
                      })()}
                    </div>
                  </div>
                  
                  {/* Show calculation result if available */}
                  {userAnswers.answers?.calculation && (
                    <div className="mb-6 p-5 bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-300 rounded-xl shadow-sm">
                      <p className="font-bold text-blue-900 mb-2 flex items-center">
                        <FaChartLine className="w-5 h-5 mr-2" />
                        {t('reporting.emissionsQuestions.emissionsCalculated') || 'Emissions Calculated'}
                      </p>
                      <div className="text-sm text-blue-800 space-y-1">
                        <p>
                          <strong>{t('reporting.emissionsQuestions.calculatedValue') || 'CO₂e'}:</strong>{' '}
                          <span className="text-lg font-bold">
                            {userAnswers.answers.calculation.co2e_tons.toFixed(2)} {userAnswers.answers.calculation.co2e_unit}
                          </span>
                        </p>
                        <p>
                          <strong>{t('reporting.emissionsQuestions.calculatedAt') || 'Calculated at'}:</strong>{' '}
                          {formatIsoTimestamp(userAnswers.answers.calculation.calculated_at) || '—'}
                        </p>
                        <p>
                          <strong>{t('reporting.emissionsQuestions.calculationMethod') || 'Method'}:</strong>{' '}
                          {userAnswers.answers.calculation.calculation_method || 'Climatiq API'}
                        </p>
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Progress Indicator */}
              {currentQuestions.length > 0 && (
                <div className="mb-8">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-semibold text-gray-700">{t('reporting.emissionsQuestions.progress')}</span>
                    <span className="text-sm text-gray-600">
                      {currentQuestions.filter(q => {
                        const answer = answers[q.field];
                        return answer !== undefined && answer !== null && answer !== '';
                      }).length} / {currentQuestions.length} {t('reporting.emissionsQuestions.answeredLabel')}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                    <div 
                      className="bg-gradient-to-r from-green-500 to-emerald-600 h-full rounded-full transition-all duration-500"
                      style={{
                        width: `${(currentQuestions.filter(q => {
                          const answer = answers[q.field];
                          return answer !== undefined && answer !== null && answer !== '';
                        }).length / currentQuestions.length) * 100}%`
                      }}
                    ></div>
                  </div>
                </div>
              )}

              <div className="space-y-8">
                {currentQuestions.map((question, index) => {
                  const hasAnswer = answers[question.field] !== undefined && 
                                   answers[question.field] !== null && 
                                   answers[question.field] !== '';
                  
                  return (
                    <div 
                      key={question.id || index} 
                      className={`p-6 rounded-xl border-2 transition-all ${
                        hasAnswer 
                          ? 'border-green-200 bg-green-50/30' 
                          : 'border-gray-200 bg-white hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-start space-x-3 mb-4">
                        <div className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center font-bold text-sm ${
                          hasAnswer 
                            ? 'bg-green-500 text-white' 
                            : 'bg-gray-200 text-gray-600'
                        }`}>
                          {index + 1}
                        </div>
                        <div className="flex-1">
                          <label className="block text-lg font-bold text-gray-800 mb-2">
                            {question.question}
                            {question.required !== false && (
                              <span className="text-red-500 ml-1">*</span>
                            )}
                            {hasAnswer && (
                              <FaCheckCircle className="inline-block w-4 h-4 text-green-600 ml-2" />
                            )}
                          </label>
                          {question.helpText && (
                            <div className="flex items-start space-x-2 mt-2 mb-4">
                              <FaInfoCircle className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                              <p className="text-sm text-gray-600">{question.helpText}</p>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="ml-11">
                        {renderQuestionInput(question)}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Action Buttons */}
              <div className="mt-10 pt-6 border-t border-gray-200 flex justify-between items-center">
                <button
                  onClick={handleBack}
                  className="px-6 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-all font-semibold flex items-center space-x-2"
                >
                  <FaArrowLeft className="w-4 h-4" />
                  <span>{t('reporting.emissionsQuestions.back')}</span>
                </button>
                <div className="flex items-center space-x-4">
                  <div className="text-sm text-gray-600">
                    {currentQuestions.filter(q => {
                      const answer = answers[q.field];
                      return answer !== undefined && answer !== null && answer !== '';
                    }).length} {t('reporting.emissionsQuestions.ofAnswered')} {currentQuestions.length} {t('reporting.emissionsQuestions.answeredLabel')}
                  </div>
                  <div className="flex gap-3">
                    <button
                      onClick={handleSave}
                      disabled={saving}
                      className="px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl hover:from-green-600 hover:to-emerald-700 transition-all font-semibold flex items-center space-x-2 disabled:opacity-50 shadow-lg hover:shadow-xl"
                    >
                      <FaSave className="w-4 h-4" />
                      <span>{saving ? t('reporting.emissionsQuestions.saving') : t('reporting.emissionsQuestions.saveAnswers')}</span>
                    </button>
                    
                    {/* Calculate button - only show for calculation categories */}
                    {selectedQuestionSet && selectedQuestionSet.category !== 'organization_context' && (
                      <button
                        onClick={handleCalculate}
                        disabled={saving}
                        className="px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-xl hover:from-blue-600 hover:to-indigo-700 transition-all font-semibold flex items-center space-x-2 disabled:opacity-50 shadow-lg hover:shadow-xl"
                      >
                        <FaChartLine className="w-4 h-4" />
                        <span>{saving ? (t('reporting.emissionsQuestions.calculating') || 'Calculating...') : (t('reporting.emissionsQuestions.calculateEmissions') || 'Calculate Emissions')}</span>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {selectedQuestionSet && currentQuestions.length === 0 && (
          <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8 text-center">
            <p className="text-gray-500">{t('reporting.emissionsQuestions.noQuestionsAvailable')}</p>
          </div>
        )}
      </div>

      {/* Search Modal for Emission Factors */}
      {showSearchModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-white">Search Emission Factors</h2>
              <button
                onClick={() => {
                  setShowSearchModal(false);
                  setSearchQuery('');
                  setSearchResults([]);
                  setSearchField(null);
                }}
                className="text-white hover:text-gray-200 transition-colors"
              >
                <FaTimes className="w-6 h-6" />
              </button>
            </div>

            {/* Search Input */}
            <div className="p-6 border-b border-gray-200">
              <div className="flex gap-3">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleSearchFactors();
                    }
                  }}
                  placeholder="Search by activity name, category, or description (e.g., 'waste landfill', 'electricity Chile')"
                  className="flex-1 px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
                />
                <button
                  onClick={handleSearchFactors}
                  disabled={searching || !searchQuery.trim()}
                  className="px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-xl hover:from-blue-600 hover:to-indigo-700 transition-all font-semibold flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FaSearch className="w-4 h-4" />
                  <span>{searching ? 'Searching...' : 'Search'}</span>
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Search for emission factors in Climatiq database. Results are filtered to public factors only.
              </p>
            </div>

            {/* Search Results */}
            <div className="flex-1 overflow-y-auto p-6">
              {searching && (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
                  <p className="text-gray-600">Searching emission factors...</p>
                </div>
              )}

              {!searching && searchResults.length > 0 && (
                <div className="space-y-3">
                  <p className="text-sm font-semibold text-gray-700 mb-4">
                    Found {searchResults.length} result(s). Click a result to select it.
                  </p>
                  {searchResults.map((factor, idx) => (
                    <div
                      key={idx}
                      onClick={() => handleSelectFactor(factor)}
                      className="p-4 border-2 border-gray-200 rounded-xl hover:border-blue-500 hover:bg-blue-50 cursor-pointer transition-all"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className="font-bold text-gray-800 mb-1">{factor.name}</h3>
                          <p className="text-sm text-gray-600 mb-2">{factor.description || 'No description available'}</p>
                          <div className="flex flex-wrap gap-2 text-xs">
                            <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full">
                              {factor.category}
                            </span>
                            {factor.region && (
                              <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full">
                                {factor.region_name || factor.region}
                              </span>
                            )}
                            {factor.year && (
                              <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded-full">
                                Year: {factor.year}
                              </span>
                            )}
                            {factor.unit && (
                              <span className="px-2 py-1 bg-orange-100 text-orange-800 rounded-full">
                                Unit: {factor.unit}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="ml-4 text-right">
                          <p className="text-xs text-gray-500 mb-1">Factor ID:</p>
                          <p className="text-xs font-mono text-blue-600 break-all max-w-xs">
                            {factor.id}
                          </p>
                          {factor.activity_id && (
                            <>
                              <p className="text-xs text-gray-500 mt-2 mb-1">Activity ID:</p>
                              <p className="text-xs font-mono text-green-600 break-all max-w-xs">
                                {factor.activity_id}
                              </p>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {!searching && searchQuery && searchResults.length === 0 && (
                <div className="text-center py-8">
                  <FaQuestionCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No results found. Try a different search term.</p>
                  <p className="text-sm text-gray-500 mt-2">
                    Examples: "waste landfill", "electricity Chile", "fuel combustion"
                  </p>
                </div>
              )}

              {!searching && !searchQuery && (
                <div className="text-center py-8">
                  <FaSearch className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">Enter a search term above to find emission factors.</p>
                  <p className="text-sm text-gray-500 mt-2">
                    Examples: "waste landfill", "electricity Chile", "fuel combustion", "transport"
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Batch / line-item table driven by question.columns (process, fugitive, batch estimates).
 * Falls back to the legacy fixed layout only when columns is empty.
 */
const BatchEstimateTable = ({ value = [], onChange, columns = [], onSearchFactor, required = false }) => {
  const useDynamicColumns = Array.isArray(columns) && columns.length > 0;

  const removeRow = (index) => {
    onChange(value.filter((_, i) => i !== index));
  };

  const updateRow = (index, field, fieldValue) => {
    const newRows = [...value];
    if (!newRows[index]) newRows[index] = {};
    newRows[index] = { ...newRows[index], [field]: fieldValue };
    onChange(newRows);
  };

  const addRow = () => {
    if (useDynamicColumns) {
      const newRow = {};
      columns.forEach((col) => {
        if (col.defaultValue !== undefined && col.defaultValue !== null && col.defaultValue !== '') {
          newRow[col.field] = col.defaultValue;
        } else if (col.type === 'checkbox') {
          newRow[col.field] = false;
        } else if (col.type === 'select' && col.options && col.options.length > 0) {
          newRow[col.field] = col.options[0].value;
        } else {
          newRow[col.field] = '';
        }
      });
      onChange([...value, newRow]);
      return;
    }
    onChange([
      ...value,
      {
        emission_factor_id: '',
        parameter_type: 'amount',
        amount: '',
        unit: 'kg',
        energy: '',
        energy_unit: 'kwh',
        money: '',
        money_unit: 'usd'
      }
    ]);
  };

  const columnVisible = (col, row) => {
    if (!col.conditional) return true;
    return String(row[col.conditional.field] ?? '') === String(col.conditional.value);
  };

  const renderDynamicCell = (col, row, rowIndex) => {
    if (!columnVisible(col, row)) {
      return <span className="text-gray-300 text-sm">—</span>;
    }
    const raw = row[col.field];
    switch (col.type) {
      case 'checkbox':
        return (
          <input
            type="checkbox"
            checked={!!raw}
            onChange={(e) => updateRow(rowIndex, col.field, e.target.checked)}
            className="w-4 h-4 rounded border-gray-300"
          />
        );
      case 'select':
        return (
          <select
            value={raw ?? ''}
            onChange={(e) => updateRow(rowIndex, col.field, e.target.value)}
            className="w-full min-w-[8rem] px-2 py-2 border border-gray-300 rounded-lg focus:border-blue-500 text-sm bg-white"
          >
            {(col.options || []).map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        );
      case 'number':
        return (
          <input
            type="number"
            value={raw === undefined || raw === null ? '' : raw}
            onChange={(e) =>
              updateRow(rowIndex, col.field, e.target.value === '' ? '' : e.target.value)
            }
            min={col.min}
            max={col.max}
            step={col.step || 1}
            className="w-full min-w-[6rem] px-2 py-2 border border-gray-300 rounded-lg focus:border-blue-500 text-sm"
          />
        );
      default:
        if (col.field === 'emission_factor_id' && col.searchable && onSearchFactor) {
          return (
            <div className="flex gap-1">
              <input
                type="text"
                value={raw ?? ''}
                onChange={(e) => updateRow(rowIndex, col.field, e.target.value)}
                placeholder={col.label || 'Factor ID'}
                className="flex-1 min-w-[6rem] px-2 py-2 border border-gray-300 rounded-lg text-sm"
              />
              <button
                type="button"
                onClick={() => onSearchFactor(rowIndex)}
                className="px-2 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-sm shrink-0"
                title="Search factors"
              >
                <FaSearch className="w-3 h-3" />
              </button>
            </div>
          );
        }
        return (
          <input
            type="text"
            value={raw ?? ''}
            onChange={(e) => updateRow(rowIndex, col.field, e.target.value)}
            placeholder={col.label || col.field}
            className="w-full min-w-[6rem] px-2 py-2 border border-gray-300 rounded-lg text-sm"
          />
        );
    }
  };

  if (useDynamicColumns) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm text-gray-600">
            Add one row per source. Required fields must be filled for each row before save/calculate.
          </p>
          <button
            type="button"
            onClick={addRow}
            className="px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg hover:from-green-600 hover:to-emerald-700 font-semibold flex items-center space-x-2"
          >
            <FaPlus className="w-4 h-4" />
            <span>Add Row</span>
          </button>
        </div>

        {value.length === 0 && (
          <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-xl bg-gray-50">
            <p className="text-gray-500 mb-2">No rows yet. Click &quot;Add Row&quot;.</p>
            {required && <p className="text-sm text-red-500">At least one row is required</p>}
          </div>
        )}

        {value.length > 0 && (
          <div className="overflow-x-auto rounded-xl border border-gray-200">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="bg-gradient-to-r from-blue-50 to-indigo-50">
                  {columns.map((col) => (
                    <th
                      key={col.field}
                      className="border border-gray-200 px-3 py-2 text-left font-semibold text-gray-700 whitespace-nowrap"
                    >
                      {col.label || col.field}
                      {col.required ? <span className="text-red-500 ml-0.5">*</span> : null}
                    </th>
                  ))}
                  <th className="border border-gray-200 px-3 py-2 text-center font-semibold text-gray-700 w-20">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {value.map((row, rowIndex) => (
                  <tr key={rowIndex} className="hover:bg-gray-50">
                    {columns.map((col) => (
                      <td key={col.field} className="border border-gray-200 px-3 py-2 align-top">
                        {renderDynamicCell(col, row || {}, rowIndex)}
                      </td>
                    ))}
                    <td className="border border-gray-200 px-2 py-2 text-center align-top">
                      <button
                        type="button"
                        onClick={() => removeRow(rowIndex)}
                        className="p-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
                        title="Remove row"
                      >
                        <FaTrash className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-600">
          Add multiple emission estimate rows. Each row will be calculated and the total will be shown.
        </p>
        <button
          type="button"
          onClick={addRow}
          className="px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg hover:from-green-600 hover:to-emerald-700 transition-all font-semibold flex items-center space-x-2"
        >
          <FaPlus className="w-4 h-4" />
          <span>Add Row</span>
        </button>
      </div>

      {value.length === 0 && (
        <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-xl bg-gray-50">
          <p className="text-gray-500 mb-4">No rows added yet. Click &quot;Add Row&quot; to start.</p>
          {required && <p className="text-sm text-red-500">At least one row is required</p>}
        </div>
      )}

      {value.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-gradient-to-r from-blue-50 to-indigo-50">
                <th className="border border-gray-300 px-4 py-3 text-left text-sm font-semibold text-gray-700">
                  Emission Factor ID
                </th>
                <th className="border border-gray-300 px-4 py-3 text-left text-sm font-semibold text-gray-700">
                  Parameter Type
                </th>
                <th className="border border-gray-300 px-4 py-3 text-left text-sm font-semibold text-gray-700">
                  Value
                </th>
                <th className="border border-gray-300 px-4 py-3 text-left text-sm font-semibold text-gray-700">
                  Unit
                </th>
                <th className="border border-gray-300 px-4 py-3 text-center text-sm font-semibold text-gray-700 w-20">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {value.map((row, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="border border-gray-300 px-4 py-3">
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={row.emission_factor_id || ''}
                        onChange={(e) => updateRow(index, 'emission_factor_id', e.target.value)}
                        placeholder="Enter factor ID"
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 text-sm"
                        required={required}
                      />
                      <button
                        type="button"
                        onClick={() => onSearchFactor && onSearchFactor(index)}
                        className="px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-all text-sm"
                        title="Search for emission factors"
                      >
                        <FaSearch className="w-3 h-3" />
                      </button>
                    </div>
                  </td>
                  <td className="border border-gray-300 px-4 py-3">
                    <select
                      value={row.parameter_type || 'amount'}
                      onChange={(e) => updateRow(index, 'parameter_type', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 text-sm"
                    >
                      <option value="amount">Amount + Unit</option>
                      <option value="energy">Energy + Unit</option>
                      <option value="money">Money + Currency</option>
                    </select>
                  </td>
                  <td className="border border-gray-300 px-4 py-3">
                    {row.parameter_type === 'amount' && (
                      <input
                        type="number"
                        value={row.amount || ''}
                        onChange={(e) => updateRow(index, 'amount', e.target.value)}
                        placeholder="Amount"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 text-sm"
                      />
                    )}
                    {row.parameter_type === 'energy' && (
                      <input
                        type="number"
                        value={row.energy || ''}
                        onChange={(e) => updateRow(index, 'energy', e.target.value)}
                        placeholder="Energy"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 text-sm"
                      />
                    )}
                    {row.parameter_type === 'money' && (
                      <input
                        type="number"
                        value={row.money || ''}
                        onChange={(e) => updateRow(index, 'money', e.target.value)}
                        placeholder="Money"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 text-sm"
                      />
                    )}
                  </td>
                  <td className="border border-gray-300 px-4 py-3">
                    {row.parameter_type === 'amount' && (
                      <input
                        type="text"
                        value={row.unit || 'kg'}
                        onChange={(e) => updateRow(index, 'unit', e.target.value)}
                        placeholder="kg, t, etc."
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 text-sm"
                      />
                    )}
                    {row.parameter_type === 'energy' && (
                      <select
                        value={row.energy_unit || 'kwh'}
                        onChange={(e) => updateRow(index, 'energy_unit', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 text-sm"
                      >
                        <option value="kwh">kWh</option>
                        <option value="mwh">MWh</option>
                      </select>
                    )}
                    {row.parameter_type === 'money' && (
                      <select
                        value={row.money_unit || 'usd'}
                        onChange={(e) => updateRow(index, 'money_unit', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 text-sm"
                      >
                        <option value="usd">USD</option>
                      </select>
                    )}
                  </td>
                  <td className="border border-gray-300 px-4 py-3 text-center">
                    <button
                      type="button"
                      onClick={() => removeRow(index)}
                      className="px-3 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-all"
                      title="Remove row"
                    >
                      <FaTrash className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {value.length > 0 && (
        <p className="text-xs text-gray-500 flex items-start space-x-1 mt-2">
          <FaInfoCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
          <span>You can add up to 100 rows. Each row will be calculated separately and the total will be shown.</span>
        </p>
      )}
    </div>
  );
};

export default EmissionsQuestionsPage;
